import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import BankLogo from '../components/BankLogo';
import { ArrowLeft, GitCompare, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ComparePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, banksRes] = await Promise.all([
        axios.get(`${API}/services?category=loan`),
        axios.get(`${API}/banks`)
      ]);
      setServices(servicesRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const toggleBank = (bankId) => {
    setSelectedBanks(prev => 
      prev.includes(bankId) 
        ? prev.filter(id => id !== bankId)
        : [...prev, bankId]
    );
  };

  const handleCompare = async () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }
    if (selectedBanks.length < 2) {
      toast.error('Please select at least 2 banks to compare');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/compare`, {
        service_id: selectedService,
        bank_ids: selectedBanks
      });
      setComparison(response.data);
    } catch (error) {
      toast.error('Failed to compare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button 
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
          data-testid="back-to-home-button"
        >
          <ArrowLeft size={20} /> Back to Home
        </button>

        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>
            Compare Banks
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569' }}>
            Compare interest rates, fees, and features across India's top banks
          </p>
        </div>

        <div className="calculator-section" style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
            <GitCompare style={{ display: 'inline', marginRight: '0.5rem', color: '#FF6B35' }} size={24} />
            Select Service & Banks
          </h2>

          <div style={{ marginBottom: '2rem' }}>
            <label className="label-text">Select Loan Service</label>
            <select 
              className="input-field" 
              value={selectedService} 
              onChange={(e) => setSelectedService(e.target.value)}
              data-testid="compare-service-select"
            >
              <option value="">-- Choose a service --</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-text">Select Banks (minimum 2)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
              {banks.map(bank => (
                <div
                  key={bank.id}
                  className={`bank-card ${selectedBanks.includes(bank.id) ? 'selected' : ''}`}
                  onClick={() => toggleBank(bank.id)}
                  style={{ padding: '1rem', textAlign: 'center' }}
                  data-testid={`compare-bank-${bank.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div style={{ marginBottom: '0.75rem' }}>
                    <BankLogo bankName={bank.name} size={60} />
                  </div>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0F172A', marginBottom: '0.25rem' }}>
                    {bank.name}
                  </h3>
                  {selectedBanks.includes(bank.id) && (
                    <CheckCircle size={18} style={{ color: '#FF6B35', marginTop: '0.25rem' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleCompare}
            className="primary-button"
            disabled={loading}
            style={{ marginTop: '2rem', width: '100%', justifyContent: 'center' }}
            data-testid="compare-button"
          >
            {loading ? 'Comparing...' : 'Compare Now'}
          </button>
        </div>

        {comparison && (
          <div className="calculator-section fade-in" data-testid="comparison-results">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
              Comparison Results: {comparison.service.name}
            </h2>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#0F172A' }}>Feature</th>
                    {comparison.comparison.map(b => (
                      <th key={b.bank_id} style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#0F172A' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <div style={{ width: '120px' }}>
                            <BankLogo bankName={b.bank_name} size={45} />
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#0F172A' }}>{b.bank_name}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Interest Rate</td>
                    {comparison.comparison.map(b => (
                      <td key={b.bank_id} style={{ padding: '1rem', fontWeight: '700', color: b.interest_rate === 'N/A' ? '#EF4444' : '#FF6B35' }}>
                        {b.interest_rate !== 'N/A' ? `${b.interest_rate}%` : 'Not Available'}
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Service Available</td>
                    {comparison.comparison.map(b => (
                      <td key={b.bank_id} style={{ padding: '1rem' }}>
                        {b.supports_service ? 
                          <CheckCircle size={20} style={{ color: '#FF6B35' }} /> : 
                          <XCircle size={20} style={{ color: '#EF4444' }} />
                        }
                      </td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Processing Fee</td>
                    {comparison.comparison.map(b => (
                      <td key={b.bank_id} style={{ padding: '1rem', color: '#0F172A' }}>{b.processing_fee}</td>
                    ))}
                  </tr>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Prepayment Charges</td>
                    {comparison.comparison.map(b => (
                      <td key={b.bank_id} style={{ padding: '1rem', color: '#0F172A' }}>{b.prepayment_charges}</td>
                    ))}
                  </tr>
                  {comparison.comparison[0].min_amount && (
                    <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                      <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Amount Range</td>
                      {comparison.comparison.map(b => (
                        <td key={b.bank_id} style={{ padding: '1rem', color: '#0F172A', fontSize: '0.875rem' }}>
                          ₹{b.min_amount?.toLocaleString()} - ₹{b.max_amount?.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#475569' }}>Apply</td>
                    {comparison.comparison.map(b => (
                      <td key={b.bank_id} style={{ padding: '1rem' }}>
                        <button
                          onClick={() => navigate(`/apply/${comparison.service.id}`)}
                          className="primary-button"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          data-testid={`apply-from-compare-${b.bank_id}`}
                        >
                          Apply
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
